import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, LogOut, ShieldAlert } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SuspendedNotice() {
  const navigate = useNavigate();

  // Read suspension info from localStorage (set during login)
  const suspensionMessage = localStorage.getItem('suspension_message') ||
    'Your account has been suspended due to suspicious activity (unusual number of claims / higher than average activity). Please contact the system administrator.';
  const username = localStorage.getItem('username') || '';
  const hospitalName = localStorage.getItem('hospital_name') || '';

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('hospital_name');
    localStorage.removeItem('suspension_message');
    localStorage.removeItem('user_status');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 flex items-center justify-center p-6">
      <div
        className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden border border-red-100"
        data-testid="suspended-notice-page"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-6 flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-full">
            <ShieldAlert className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Account Suspended</h1>
            <p className="text-red-100 text-sm mt-1">Access to this account is currently restricted</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          <div className="flex gap-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">
                Suspicious activity detected
              </p>
              <p className="text-sm text-amber-800 leading-relaxed" data-testid="suspension-reason">
                {suspensionMessage}
              </p>
            </div>
          </div>

          {/* Account info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Username:</span>
              <span className="font-semibold text-gray-900" data-testid="suspended-username">
                {username || 'N/A'}
              </span>
            </div>
            {hospitalName && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Hospital:</span>
                <span className="font-semibold text-gray-900">{hospitalName}</span>
              </div>
            )}
          </div>

          {/* What to do */}
          <div>
            <h2 className="font-semibold text-gray-900 mb-2">What can I do?</h2>
            <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
              <li>Contact the system administrator to discuss the flagged activity.</li>
              <li>Once your account is reviewed and reactivated, you will be able to log in normally.</li>
              <li>For urgent matters, reach out to your hospital&apos;s administrator directly.</li>
            </ul>
          </div>

          {/* Action */}
          <div className="pt-4 border-t border-gray-200">
            <Button
              onClick={handleLogout}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="suspended-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
